import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Award, Users, Briefcase, Scale, Shield, Trophy, CheckCircle2, ChevronRight, Sparkles, Crown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_META = [
  {
    key: 'public',
    name: 'Public Voting',
    weight: 0.15,
    icon: Users,
    input: '⭐ 1–5 stars',
    blurb: 'Everyday travellers who have experienced the property contribute the popular voice of the awards.',
    color: '#9CA3AF',
  },
  {
    key: 'partner',
    name: 'Professional Partners',
    weight: 0.20,
    icon: Briefcase,
    input: '1–10 rating',
    blurb: 'Approved industry partners — hoteliers, agencies, and trade members — bring informed peer evaluation.',
    color: '#B59B5F',
  },
  {
    key: 'jury',
    name: 'Independent Jury',
    weight: 0.30,
    icon: Scale,
    input: '0–100 criteria scoring',
    blurb: 'The International Advisory Board appoints a specialist panel that scores each candidate against five rigorous criteria.',
    color: '#D4AF37',
  },
  {
    key: 'admin',
    name: 'Travel-Awards.Org',
    weight: 0.35,
    icon: Shield,
    input: 'Final review (0–100)',
    blurb: 'A final review by Travel-Awards.Org confirms operational integrity, licensing, and authenticity of every award.',
    color: '#F5E6B3',
  },
];

const TIERS = [
  { tier: 'Gold',    range: '90 – 100',  color: 'bg-[#D4AF37] text-[#0A0A0A]', icon: Crown,  ring: 'ring-[#D4AF37]/40' },
  { tier: 'Silver',  range: '80 – 89.99', color: 'bg-[#C0C0C0] text-[#0A0A0A]', icon: Trophy, ring: 'ring-[#C0C0C0]/30' },
  { tier: 'Bronze',  range: '70 – 79.99', color: 'bg-[#CD7F32] text-white',     icon: Trophy, ring: 'ring-[#CD7F32]/30' },
  { tier: 'Nominee', range: 'below 70',   color: 'bg-white/10 text-white/80',   icon: Award,  ring: 'ring-white/10' },
];

export default function MethodologyPage() {
  const [criteria, setCriteria] = useState([
    { key: 'hospitality',  label: 'Exceptional Hospitality',                                       weight: 0.30 },
    { key: 'satisfaction', label: 'Outstanding guest satisfaction & service consistency',           weight: 0.25 },
    { key: 'facilities',   label: 'Clean, safe, and well-maintained facilities',                    weight: 0.20 },
    { key: 'community',    label: 'Stronger community and sustainability engagement',               weight: 0.15 },
    { key: 'fnb',          label: 'Enhanced food & beverage offering',                              weight: 0.10 },
  ]);

  useEffect(() => {
    let alive = true;
    axios.get(`${API_URL}/api/rating/config`)
      .then((res) => alive && res.data?.jury_criteria && setCriteria(res.data.jury_criteria))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <main className="min-h-screen pt-24 pb-20 px-4 sm:px-6 lg:px-8" data-testid="methodology-page">
      <div className="max-w-6xl mx-auto">
        {/* Hero */}
        <section className="text-center mb-20">
          <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-4 flex items-center justify-center gap-2">
            <Sparkles className="w-4 h-4" />
            Travel Awards Methodology
          </p>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl font-medium text-white mb-6 leading-tight">
            A Trusted, Transparent
            <br />
            <span className="text-gradient-gold">Awards Framework</span>
          </h1>
          <p className="text-white/60 max-w-3xl mx-auto leading-relaxed text-base sm:text-lg">
            Every Travel Awards & TWBL Awards recognition is the product of a structured,
            multi-source evaluation. Four independent constituencies contribute to a single
            normalized score — eliminating bias, surfacing true excellence, and ensuring
            credibility for every winner.
          </p>
        </section>

        {/* Formula card */}
        <section className="mb-20" data-testid="formula-section">
          <div className="bg-gradient-to-br from-[#161616] via-[#101010] to-[#0A0A0A] border border-[#D4AF37]/20 rounded-2xl p-8 sm:p-12 shadow-[0_0_60px_rgba(212,175,55,0.06)]">
            <div className="text-center mb-10">
              <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-3">The Formula</p>
              <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white">
                Final Score, normalized to 100
              </h2>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 font-['Outfit'] text-sm sm:text-lg text-white/80">
              <Pill label="Public" mult="× 0.15" tone="#9CA3AF" />
              <PlusSep />
              <Pill label="Partner" mult="× 0.20" tone="#B59B5F" />
              <PlusSep />
              <Pill label="Jury" mult="× 0.30" tone="#D4AF37" />
              <PlusSep />
              <Pill label="TAO" mult="× 0.35" tone="#F5E6B3" />
            </div>
            <p className="text-center text-white/50 text-sm mt-8 max-w-2xl mx-auto">
              All scores are normalized to a 0–100 scale before weighting. If a constituency
              has not yet rated an award, its weight is fairly redistributed across the
              remaining sources so newer entries are never penalized.
            </p>
          </div>
        </section>

        {/* 4 constituencies */}
        <section className="mb-20">
          <div className="flex items-center gap-3 mb-10">
            <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80">
              The Four Voting Sources
            </p>
            <div className="flex-1 h-px bg-gradient-to-r from-[#D4AF37]/30 to-transparent" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {SOURCE_META.map((s) => (
              <div
                key={s.key}
                className="relative bg-[#121212] border border-white/5 rounded-2xl p-7 hover:border-[#D4AF37]/30 transition-all overflow-hidden group"
                data-testid={`source-${s.key}`}
              >
                {/* Weight ribbon */}
                <div className="absolute top-0 right-0 bg-[#0A0A0A] border-l border-b border-[#D4AF37]/30 px-4 py-2 rounded-bl-2xl">
                  <span className="font-['Playfair_Display'] text-2xl text-[#D4AF37]">
                    {(s.weight * 100).toFixed(0)}%
                  </span>
                </div>

                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-5"
                  style={{ background: `${s.color}22`, color: s.color }}
                >
                  <s.icon className="w-6 h-6" />
                </div>

                <h3 className="font-['Playfair_Display'] text-2xl text-white mb-2">
                  {s.name}
                </h3>
                <p className="text-[#D4AF37]/80 text-xs uppercase tracking-[0.2em] mb-4">
                  Input · {s.input}
                </p>
                <p className="text-white/60 text-sm leading-relaxed">
                  {s.blurb}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Jury criteria */}
        <section className="mb-20" data-testid="jury-criteria-section">
          <div className="flex items-center gap-3 mb-8">
            <Scale className="w-5 h-5 text-[#D4AF37]" />
            <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80">
              Jury Criteria
            </p>
            <div className="flex-1 h-px bg-gradient-to-r from-[#D4AF37]/30 to-transparent" />
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white mb-3">
            Five weighted dimensions
          </h2>
          <p className="text-white/50 max-w-3xl mb-10">
            Each jury member evaluates a candidate on these five dimensions (0–100 each).
            The dimensions are weighted to produce the jury&apos;s composite contribution.
          </p>
          <div className="space-y-3">
            {criteria.map((c, i) => (
              <div
                key={c.key}
                className="flex items-center gap-5 bg-[#121212] border border-white/5 rounded-xl p-5 hover:border-[#D4AF37]/20 transition-all"
                data-testid={`criterion-${c.key}`}
              >
                <span className="font-['Playfair_Display'] text-2xl text-[#D4AF37]/60 w-8 shrink-0">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium leading-snug">{c.label}</p>
                  <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#D4AF37] to-[#E8C76A]"
                      style={{ width: `${c.weight * 100}%` }}
                    />
                  </div>
                </div>
                <span className="font-['Playfair_Display'] text-2xl text-white shrink-0 tabular-nums">
                  {(c.weight * 100).toFixed(0)}%
                </span>
              </div>
            ))}
          </div>
        </section>

        {/* Tiers */}
        <section className="mb-20" data-testid="tiers-section">
          <div className="flex items-center gap-3 mb-8">
            <Trophy className="w-5 h-5 text-[#D4AF37]" />
            <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80">
              Award Levels
            </p>
            <div className="flex-1 h-px bg-gradient-to-r from-[#D4AF37]/30 to-transparent" />
          </div>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl font-medium text-white mb-3">
            Score thresholds — no forced ranking
          </h2>
          <p className="text-white/50 max-w-3xl mb-10">
            Levels are awarded on merit. Multiple winners can achieve the same level in a
            category, and entries below 70 remain official Nominees.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TIERS.map((t) => (
              <div
                key={t.tier}
                className={`p-7 rounded-2xl bg-[#121212] border border-white/5 ring-1 ${t.ring} text-center`}
                data-testid={`tier-${t.tier.toLowerCase()}`}
              >
                <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-[0.2em] mb-5 ${t.color}`}>
                  <t.icon className="w-3.5 h-3.5" />
                  {t.tier}
                </div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Score Range</p>
                <p className="font-['Playfair_Display'] text-2xl text-white">{t.range}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Governance / closing */}
        <section className="bg-gradient-to-br from-[#161616] via-[#101010] to-[#0A0A0A] border border-[#D4AF37]/20 rounded-2xl p-8 sm:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-start">
            <div>
              <p className="uppercase text-xs tracking-[0.3em] text-[#D4AF37]/80 mb-3">
                Governance
              </p>
              <h2 className="font-['Playfair_Display'] text-3xl font-medium text-white mb-4">
                Selected, supervised, and audited
              </h2>
              <p className="text-white/60 leading-relaxed">
                Jury members are selected by the Travel Awards International Advisory Board
                of Directors. Every rating is logged, attributable to its source, and
                recomputed automatically. The result is a methodology that is open, fair,
                and resistant to gaming.
              </p>
            </div>
            <ul className="space-y-3">
              {[
                'One score per user per award, per source',
                'Live recomputation after every rating',
                'Missing constituencies fairly redistributed',
                'Auditable rating history retained on every entry',
                'Public scores published; jury & admin scores remain confidential',
              ].map((line, i) => (
                <li key={i} className="flex items-start gap-3 text-white/80 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-[#D4AF37] mt-0.5 shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>
    </main>
  );
}

function Pill({ label, mult, tone }) {
  return (
    <div className="flex flex-col items-center gap-1">
      <div
        className="px-5 py-3 rounded-full border text-white font-medium"
        style={{ background: `${tone}1f`, borderColor: `${tone}55` }}
      >
        {label}
      </div>
      <span className="text-xs text-white/50 tabular-nums">{mult}</span>
    </div>
  );
}

function PlusSep() {
  return (
    <ChevronRight className="w-5 h-5 text-[#D4AF37]/60" />
  );
}
